/* Nick's Aquarium: the In Stock / Out of Stock collection panels.
 *
 * Moved out of an inline <script> in sections/main-collection.liquid on 2026-08-23. It was
 * ~31KB of JavaScript written into the HTML of every collection page and re-sent on every
 * view; as a theme asset it is fetched once and cached. The code is unchanged - it was
 * already wrapped end-to-end in {% raw %}, so it contained no Liquid. Every value it needs
 * still comes from the data-* attributes on <fish-collection-panels>.
 */
(() => {
  if (window.customElements.get('fish-collection-panels')) return;

  class FishCollectionPanels extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.collectionUrl = (this.dataset.collectionUrl || window.location.pathname).split('?')[0];
      this.totalPages = parseInt(this.dataset.totalPages || '1', 10) || 1;
      this.sectionId = this.dataset.sectionId || '';

      this.inGrid = this.querySelector('[data-fish-grid="in-stock"]');
      this.outGrid = this.querySelector('[data-fish-grid="out-of-stock"]');
      this.outScroll = this.querySelector('[data-fish-scroll="out-of-stock"]');
      this.outSentinel = this.querySelector('[data-fish-sentinel="out-of-stock"]');
      this.outStatus = this.querySelector('[data-fish-status="out-of-stock"]');

      // Nothing out-of-stock is server-rendered any more, so start from page 1. Those
      // pages are requested with filter.v.availability=0, i.e. only out-of-stock
      // products, so every page is full rather than a handful sifted from a mixed page.
      this.outSection = this.querySelector('[data-fish-panel="out-of-stock"]');
      this.seenOut = new Set();
      this.nextPage = 1;
      this.loading = false;
      this.outDone = false;

      this.setupControls();
      this.hoistInStockHeading();
      this.stripQuickAdds(this);
      // In stock first, complete; only then does the out-of-stock panel appear and begin
      // filling. Deliberately sequential — the out-of-stock set is the least urgent
      // content on the page and shouldn't compete for connections with the in-stock one.
      this.loadAllInStock().then(() => this.setupOutObserver());
    }

    // Put the "In Stock Now" heading into the white toolbar row, left-aligned, opposite
    // the sort/price controls. Desktop only: the toolbar bar is hidden on mobile (its
    // controls move to the filter drawer), so on mobile the panel keeps its own heading
    // (a CSS media query hides the in-panel one only on desktop).
    hoistInStockHeading() {
      const bar = document.querySelector('.facets--horizontal .facets__form');
      const title = this.querySelector('[data-fish-panel="in-stock"] .fish-panel__title');
      if (!bar || !title || bar.querySelector('.lv-bar-title')) return;
      const heading = document.createElement('h4');
      heading.className = 'lv-bar-title';
      heading.textContent = title.textContent.trim();
      bar.insertBefore(heading, bar.firstChild);
    }

    // ---- Client-side sort + price filter (marine page). The custom toolbar
    // (snippets/livestock-sorting.liquid) has no server round-trip; it reorders and
    // hides cards in the In Stock and Out of Stock panels independently, from data-*
    // attributes on each <li> (snippets/livestock-sort-data.liquid). Out-of-stock
    // re-applies as pages load.
    setupControls() {
      this.currentSort = '';
      this.priceMin = null;
      this.priceMax = null;
      this.searchTerm = '';
      this._livestockRoot = this.closest('.livestock-collection');

      // Custom sort dropdown (rendered in the bar and, on mobile, the filter drawer).
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-lv-sort-toggle]');
        if (toggle) {
          const root = toggle.closest('[data-lv-sort]');
          const willOpen = !root.classList.contains('is-open');
          this.closeAllSortMenus();
          if (willOpen) {
            root.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
          }
          return;
        }
        const option = e.target.closest('[data-lv-sort-option]');
        if (option) {
          this.currentSort = option.getAttribute('data-value') || '';
          this.setSortLabel(option.textContent.trim(), this.currentSort);
          this.closeAllSortMenus();
          this.refreshAll();
          return;
        }
        if (!e.target.closest('[data-lv-sort]')) this.closeAllSortMenus();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAllSortMenus();
      });

      // Inline price Min/Max filter (debounced; kept in sync across instances).
      let debounce;
      const onPrice = (source) => {
        const isMin = source.hasAttribute('data-lv-price-min');
        document.querySelectorAll(isMin ? '[data-lv-price-min]' : '[data-lv-price-max]').forEach((i) => {
          if (i !== source) i.value = source.value;
        });
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          this.readPrice();
          this.refreshAll();
        }, 250);
      };
      document.querySelectorAll('[data-lv-price-min], [data-lv-price-max]').forEach((inp) => {
        inp.addEventListener('input', () => onPrice(inp));
      });

      // Inline search: filters this page's cards by name (common + scientific), debounced
      // and kept in sync across instances (toolbar + mobile drawer).
      let searchDebounce;
      const onSearch = (source) => {
        document.querySelectorAll('[data-lv-search]').forEach((i) => {
          if (i !== source) i.value = source.value;
        });
        // On the first search, pull in the whole out-of-stock set so search covers every
        // product, not just the lazy-loaded ones. (In-stock is already fully loaded.)
        if ((source.value || '').trim()) this.loadAllOutOfStock();
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
          this.searchTerm = (source.value || '').trim().toLowerCase();
          this.refreshAll();
        }, 200);
      };
      document.querySelectorAll('[data-lv-search]').forEach((inp) => {
        inp.addEventListener('input', () => onSearch(inp));
      });
    }

    closeAllSortMenus() {
      document.querySelectorAll('[data-lv-sort].is-open').forEach((el) => {
        el.classList.remove('is-open');
        const t = el.querySelector('[data-lv-sort-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    setSortLabel(label, value) {
      document.querySelectorAll('[data-lv-sort]').forEach((el) => {
        const v = el.querySelector('[data-lv-sort-btn-value]');
        if (v) v.textContent = label;
        el.querySelectorAll('[data-lv-sort-option]').forEach((o) => {
          o.setAttribute('aria-selected', o.getAttribute('data-value') === value ? 'true' : 'false');
        });
      });
    }

    readPrice() {
      const toCents = (el) => {
        if (!el) return null;
        const n = parseFloat(String(el.value).replace(/[^\d.]/g, ''));
        return Number.isNaN(n) ? null : Math.round(n * 100);
      };
      this.priceMin = toCents(document.querySelector('[data-lv-price-min]'));
      this.priceMax = toCents(document.querySelector('[data-lv-price-max]'));
    }

    refreshAll() {
      this.refreshGrid(this.inGrid);
      this.refreshGrid(this.outGrid);
      this.updateSearchState();
      // Hiding cards is not a childList change, so the title equalizer (which keys off
      // per-row membership and listens for resize) won't otherwise re-run.
      window.dispatchEvent(new Event('resize'));
    }

    // While a search is active, hide the "In Stock" / "Out of Stock" headings so only the
    // matching results show, and hide any panel that has no matches. Cleared automatically
    // when the search box is emptied.
    updateSearchState() {
      const searching = !!this.searchTerm;
      if (this._livestockRoot) this._livestockRoot.classList.toggle('lv-searching', searching);
      this.querySelectorAll('.fish-panel').forEach((panel) => {
        const grid = panel.querySelector('ul.fish-panel__grid');
        const hasVisible = grid
          ? Array.from(grid.children).some((li) => li.tagName === 'LI' && !li.classList.contains('lv-hide'))
          : false;
        panel.classList.toggle('lv-panel-empty', !hasVisible);
      });
    }

    refreshGrid(grid) {
      this.applySortGrid(grid);
      this.applyFilters(grid);
    }

    applyFilters(grid) {
      if (!grid) return;
      const term = this.searchTerm || '';
      const items = Array.from(grid.children).filter((el) => el.tagName === 'LI');
      items.forEach((li) => {
        const cents = Number(li.dataset.price);
        let hide = false;
        if (this.priceMin != null && !Number.isNaN(cents) && cents < this.priceMin) hide = true;
        if (this.priceMax != null && !Number.isNaN(cents) && cents > this.priceMax) hide = true;
        if (term && (li.dataset.search || '').indexOf(term) === -1) hide = true;
        li.classList.toggle('lv-hide', hide);
      });
    }

    // The theme's hover "quick add" is CSS-hidden in the fish panels (in-stock cards use
    // the Favourite + Add to Cart row, out-of-stock use Notify Me), so each card ships a
    // redundant, never-shown add-to-cart form. Remove those nodes to lighten the page -
    // fewer <product-form-component> instances to react to every cart update. Scoped to
    // the fish panels only, so quick add is untouched elsewhere on the site.
    stripQuickAdds(root) {
      if (!root) return;
      root.querySelectorAll('.quick-add').forEach((el) => el.remove());
    }

    applySortGrid(grid) {
      if (!grid) return;
      const items = Array.from(grid.children).filter((el) => el.tagName === 'LI');
      if (!items.length) return;
      // Stamp a stable original-order sequence so "Sort" (default) restores load order
      // and equal values keep a deterministic order. New (lazy-loaded) cards continue it.
      if (grid._seq == null) grid._seq = 0;
      items.forEach((li) => {
        if (li.dataset.seq === undefined || li.dataset.seq === '') li.dataset.seq = String(grid._seq++);
      });

      const [key, dir] = (this.currentSort || '').split(':');
      const seqOf = (li) => Number(li.dataset.seq);
      const parseNum = (s) => {
        if (!s) return NaN;
        const m = String(s).match(/[\d.]+/);
        return m ? parseFloat(m[0]) : NaN;
      };
      const numFor = (li) => {
        switch (key) {
          case 'price': return Number(li.dataset.price);
          case 'care': return li.dataset.care === '' ? NaN : Number(li.dataset.care);
          case 'reef': return li.dataset.reef === '' ? NaN : Number(li.dataset.reef);
          case 'size': return parseNum(li.dataset.size);
          case 'tank': return parseNum(li.dataset.tank);
          default: return NaN;
        }
      };

      const sorted = items.slice().sort((a, b) => {
        if (!key) return seqOf(a) - seqOf(b);
        if (key === 'title') {
          const r = (a.dataset.title || '').localeCompare(b.dataset.title || '');
          if (r !== 0) return dir === 'desc' ? -r : r;
          return seqOf(a) - seqOf(b);
        }
        const na = numFor(a);
        const nb = numFor(b);
        const am = Number.isNaN(na);
        const bm = Number.isNaN(nb);
        if (am && bm) return seqOf(a) - seqOf(b);
        if (am) return 1; // unknown values always sort last, in both directions
        if (bm) return -1;
        if (na !== nb) return dir === 'desc' ? nb - na : na - nb;
        return seqOf(a) - seqOf(b);
      });

      const frag = document.createDocumentFragment();
      sorted.forEach((li) => frag.appendChild(li));
      grid.appendChild(frag);
    }

    async fetchDoc(url) {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      const text = await res.text();
      return new DOMParser().parseFromString(text, 'text/html');
    }

    // Build a background-fetch URL.
    //
    // The default path is the cards-only endpoint: templates/collection.cards.json
    // renders sections/main-collection-cards.liquid, which emits the <li> product cards
    // and nothing else — no facets, no toolbars, no crawler scripts, no stylesheets.
    // Only the <li> nodes were ever used out of these responses anyway.
    //
    // This is the fix for the note left on 2026-07-27, which read: DO NOT switch this to
    // the bare ?view=cards endpoint — it was much faster (801ms/187KB vs 1262ms/889KB)
    // but the cards came back with no images and no price/meta, because
    // `content_for 'block', type: '_product-card'` rendered there picks up the block's
    // DEFAULT settings, not the configured settings in templates/collection.json.
    // collection.cards.json carries a verbatim copy of those block settings, so the cards
    // it returns are identical to the server-rendered ones. Keep the two in sync when the
    // product card is edited in the theme editor.
    //
    // pageUrl(page, extra, true) returns the old full-section URL, used only as a
    // fallback — see fetchPage().
    pageUrl(page, extra, legacy) {
      let url = this.collectionUrl + '?page=' + page;
      if (extra) url += '&' + extra;
      if (legacy) {
        if (this.sectionId) url += '&section_id=' + encodeURIComponent(this.sectionId);
      } else {
        // 30 Sep 2026: no section_id. "section_id=cards" returned 404 on every collection, so
        // every fetch silently fell back to the ~1.8MB full-section render below. The cards
        // template now renders without the theme layout ("layout": false), so the bare view
        // returns just the cards.
        url += '&view=cards';
      }
      return url;
    }

    // Fetch one page of cards from the light endpoint, falling back to the full-section
    // URL if that endpoint ever returns something without a grid in it (a renamed
    // template or section, say). The fallback latches for the rest of the page view, so
    // the shopper sees a slower panel rather than an empty one — never a broken page.
    async fetchPage(page, extra) {
      if (!this._legacyOnly) {
        try {
          const doc = await this.fetchDoc(this.pageUrl(page, extra));
          if (doc.querySelector('[data-fish-grid]')) return doc;
        } catch (e) {
          // fall through to the legacy endpoint below
        }
        console.warn('[fish-panels] cards endpoint unavailable, falling back to full section');
        this._legacyOnly = true;
      }
      return this.fetchDoc(this.pageUrl(page, extra, true));
    }

    // In-stock is small; pull the complete set via Shopify's availability filter
    // (which returns only in-stock products).
    // Paint the in-stock grid from the ordered set gathered so far. Called after EVERY
    // page arrives, not just at the end, so the first page shows immediately.
    //
    // Reconciles rather than rebuilds. The original code did replaceChildren() with fresh
    // copies of every card, which destroyed the server-rendered cards already on screen —
    // their images re-downloaded and the grid visibly flashed and re-laid-out ("loads
    // twice"). The worse the mismatch the worse the flash: a collection whose first 48
    // products hold only 4 in-stock items showed 4 cards, then blew them away to draw 86.
    //
    // Instead: walk the ordered set and MOVE the existing node when that product is
    // already on screen (appendChild moves rather than clones, so the <img> keeps its
    // decoded image and never refetches), importing only genuinely new cards. That makes
    // this safe to call repeatedly — cards already shown never flicker.
    // 2026-08-23 (scroll lag): the theme marks the first image of every product card
    // loading="eager" — sensible for a normal grid of 24, wrong for a panel that streams in
    // hundreds. The browser then queues every one of them in document order, so the card you have
    // just scrolled to waits behind several hundred images above it: you reach it and the photo
    // is not there yet.
    //
    // Cards arriving by fetch are, by definition, being appended below what is on screen, so they
    // are switched to lazy. Nothing is skipped and nothing is lost — lazy only defers images that
    // are OFF screen, so anything already in view still loads at once, and everything else now
    // loads in the order you actually scroll to it instead of the order it happens to sit in the
    // document. Server-rendered cards are left exactly as they are.
    //
    // This runs on the node inside the parsed response, BEFORE importNode. A document from
    // DOMParser has no browsing context, so nothing in it has begun downloading and the attribute
    // is carried across by the import. Setting it after importing would be too late: the copy
    // lands in the live document and the eager fetch can already be away.
    deferCardImages(node) {
      node.querySelectorAll('img').forEach((img) => {
        if (img.getAttribute('loading') === 'lazy') return;
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      });
      return node;
    }

    syncInStock(collected) {
      const existing = new Map();
      Array.from(this.inGrid.children).forEach((li) => {
        if (li.dataset && li.dataset.productId) existing.set(li.dataset.productId, li);
      });
      const frag = document.createDocumentFragment();
      collected.forEach((li) => {
        const id = li.dataset.productId;
        const current = id ? existing.get(id) : null;
        if (current) {
          existing.delete(id);
          frag.appendChild(current);
        } else {
          frag.appendChild(document.importNode(this.deferCardImages(li), true));
        }
      });
      this.inGrid.replaceChildren(frag);
      this.stripQuickAdds(this.inGrid);
      this.refreshGrid(this.inGrid);
    }

    // 30 Sep 2026: timed coral drop. Cards for products whose custom.release_at is still in the
    // future, from templates/collection.coming.json (sections/main-collection-coming.liquid).
    // Page 1 first; if the out-of-stock set runs past 250 products the remaining pages (at most
    // 7 more) are fetched in parallel. Never throws; an empty list just means no drop is running
    // in this collection.
    async fetchComing() {
      const out = [];
      const grab = async (p) => {
        const res = await fetch(this.collectionUrl + '?view=coming&filter.v.availability=0&page=' + p, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const text = res.ok ? await res.text() : '';
        return new DOMParser().parseFromString(text, 'text/html').querySelector('[data-fish-grid="coming"]');
      };
      const take = (grid) => {
        if (grid) grid.querySelectorAll(':scope > li.product-grid__item').forEach((li) => out.push(li));
      };
      try {
        const first = await grab(1);
        take(first);
        if (first && first.dataset.next === '1') {
          const pages = Math.min(parseInt(first.dataset.pages || '1', 10) || 1, 8);
          const rest = [];
          for (let p = 2; p <= pages; p += 1) rest.push(grab(p).catch(() => null));
          (await Promise.all(rest)).forEach(take);
        }
      } catch (e) {
        console.warn('[fish-panels] coming load failed', e);
      }
      return out;
    }

    // Resolve page 1 of the in-stock set from the request started inline during HTML
    // parsing. Falls back to a normal fetch if that early request is missing or failed.
    async firstInStockDoc() {
      const early = window.__fishInStockPage1;
      if (early) {
        const text = await early;
        if (text) {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          if (doc.querySelector('[data-fish-grid]')) return doc;
        }
      }
      return this.fetchPage(1, 'filter.v.availability=1');
    }

    // 2026-08-23 (speed): the crawl used to fire as soon as the element upgraded — six requests
    // in parallel — while the browser was still fetching the page's own stylesheet and the first
    // screen of product images. Nothing about the end state has changed: the complete in-stock
    // set still loads, so the panel, its search and its sort all still cover every product. What
    // changed is the order. Page 1 paints first; pages 2 onwards wait until the browser has
    // finished the page's own work. BATCH also drops from 6 to 3 so the crawl leaves connections
    // free for images rather than saturating them.
    async loadAllInStock() {
      this._inStockCollected = [];
      this._inStockSeen = new Set();

      const status = this.querySelector('[data-fish-status="in-stock"]');
      // The server only renders page 1 of the UNFILTERED collection, so the in-stock panel starts
      // with however many of those first 48 products happen to be in stock — sometimes most of
      // them, sometimes none. Show a quiet status while the real set loads so an initially-empty
      // panel doesn't read as "nothing here".
      if (status) status.textContent = 'Loading stock…';

      try {
        // 30 Sep 2026: corals in a timed drop (stock 0, custom.release_at in the future) count
        // as in stock. Fetched alongside in-stock page 1 so neither waits on the other, then
        // added after that page's cards, so nothing already on screen moves.
        const comingPromise = this.fetchComing();

        const mayHaveMore = await this.loadInStockPages(1, 1);

        const coming = await comingPromise;
        let comingAdded = 0;
        coming.forEach((li) => {
          const id = li.dataset.productId;
          if (id && this._inStockSeen.has(id)) return;
          if (id) this._inStockSeen.add(id);
          this._inStockCollected.push(li);
          comingAdded += 1;
        });
        if (comingAdded) this.syncInStock(this._inStockCollected);

        if (mayHaveMore) {
          await this.whenIdle();
          await this.loadInStockPages(2, 30);
        }
      } catch (e) {
        // loadInStockPages already swallows its own failures; this is a belt-and-braces guard so
        // that nothing can reject out of here. connectedCallback chains setupOutObserver() off
        // this promise, so a rejection would take the whole Out of Stock panel down with it.
        console.warn('[fish-panels] in-stock load failed', e);
      } finally {
        if (status) status.textContent = '';
        this.syncInStockEmpty();
      }
    }

    // Resolves once the browser has finished loading the page itself: after the load event if it
    // has not fired yet, then on the first idle callback. The 2s timeout means a permanently busy
    // page still proceeds rather than never crawling at all.
    whenIdle() {
      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        const idle = () => {
          if ('requestIdleCallback' in window) window.requestIdleCallback(finish, { timeout: 2000 });
          else window.setTimeout(finish, 200);
        };

        // Hard ceiling, and it matters. Everything downstream hangs on this resolving: in-stock
        // pages 2 onwards, the whole Out of Stock panel (setupOutObserver is chained off
        // loadAllInStock), and the "Loading stock…" status. A page that never finishes settling
        // must not mean a collection permanently missing products.
        window.setTimeout(finish, 3000);

        // Wait for the first paint rather than for the load event. Holding back until every image
        // on the page had finished meant the crawl started late on a phone, and a shopper
        // scrolling briskly could outrun it. Two frames is enough to let the first screen paint
        // without making the rest of the collection wait on it.
        window.requestAnimationFrame(() => window.requestAnimationFrame(idle));
      });
    }

    // Fetch in-stock pages `from`..`to`, painting each as it arrives. Returns false once the end
    // of the set has been reached (empty page, short page, or all duplicates), true if there may
    // still be more to fetch.
    async loadInStockPages(from, to) {
      const PAGE_SIZE = 48;
      const collected = this._inStockCollected;
      const seen = this._inStockSeen;

      const take = (doc) => {
        if (!doc) return 0;
        const items = doc.querySelectorAll('[data-fish-grid="in-stock"] > li.product-grid__item');
        let newCount = 0;
        items.forEach((li) => {
          const id = li.dataset.productId;
          if (id && seen.has(id)) return;
          if (id) seen.add(id);
          collected.push(li);
          newCount += 1;
        });
        return newCount;
      };

      // Pages are fetched a batch at a time in parallel rather than one-after-another. Results are
      // walked in page order and the first empty / short / all-duplicate page ends the crawl,
      // discarding any later pages fetched speculatively in the same batch.
      //
      // Each page is painted AS IT ARRIVES (syncInStock below) instead of holding the grid back
      // until the whole crawl finishes. Reconciling is non-destructive, so painting repeatedly
      // costs nothing visually.
      const BATCH = 3;
      let done = false;
      try {
        for (let start = from; start <= to && !done; start += BATCH) {
          const pages = [];
          for (let p = start; p < start + BATCH && p <= to; p += 1) pages.push(p);
          const docs = await Promise.all(
            pages.map((p) =>
              // Page 1 was already requested inline while the HTML was still parsing (see the
              // <script> above the panels), so it is usually resolved by the time we get here —
              // reuse it instead of firing the same request a second time.
              (p === 1 ? this.firstInStockDoc() : this.fetchPage(p, 'filter.v.availability=1'))
                .catch(() => null)
            )
          );
          for (let i = 0; i < docs.length; i += 1) {
            const doc = docs[i];
            if (!doc) { done = true; break; }
            const items = doc.querySelectorAll('[data-fish-grid="in-stock"] > li.product-grid__item');
            if (items.length === 0) { done = true; break; }
            const newCount = take(doc);
            if (newCount > 0) this.syncInStock(collected);
            if (newCount === 0 || items.length < PAGE_SIZE) { done = true; break; }
          }
        }
        if (collected.length) this.syncInStock(collected);
      } catch (e) {
        // Keep whatever is already on screen as a graceful fallback.
        console.warn('[fish-panels] in-stock load failed', e);
        done = true;
      }
      return !done;
    }

    // Reveal the "we don't have any in stock" line only when the crawl is over and the
    // grid really is empty. Called from the finally above (and after any repaint) so it
    // can never flash while pages are still arriving.
    syncInStockEmpty() {
      const empty = this.querySelector('[data-fish-empty="in-stock"]');
      if (!empty) return;
      const grid = this.querySelector('[data-fish-grid="in-stock"]');
      const count = grid ? grid.querySelectorAll('li.product-grid__item').length : 0;
      empty.hidden = count > 0;
    }

    setupOutObserver() {
      if (this.outDone || !this.outSentinel) return;
      // The panel is hidden until it has something in it, so the sentinel has no layout
      // and would never intersect. Load the first page eagerly; the observer then takes
      // over for the rest as the shopper scrolls.
      this.loadNextOutPage();
    }

    startOutObserver() {
      if (this.observer || !this.outSentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((en) => en.isIntersecting)) this.loadNextOutPage();
        },
        // 2400px rather than 600px: on a phone 600px is about half a screen of warning, which a
        // normal scroll outruns before the request comes back. Fetching three screens ahead means
        // the next cards are already there when you reach them. Each response is a fraction of
        // what it used to weigh, so reading further ahead costs little.
        { root: null, rootMargin: '2400px 0px' }
      );
      this.observer.observe(this.outSentinel);
    }

    sentinelWithinReach() {
      if (!this.outSentinel) return false;
      const s = this.outSentinel.getBoundingClientRect();
      return s.top <= window.innerHeight + 2400;
    }

    async loadNextOutPage() {
      if (this.loading || this.outDone) return;
      this.loading = true;
      if (this.outStatus) this.outStatus.textContent = 'Loading more…';
      try {
        // filter.v.availability=0 returns ONLY out-of-stock products, so each page is a
        // full 48 rather than however many happened to be unavailable on a mixed page.
        const doc = await this.fetchPage(this.nextPage, 'filter.v.availability=0');
        const items = doc.querySelectorAll('[data-fish-grid="out-of-stock"] > li.product-grid__item');
        const frag = document.createDocumentFragment();
        let added = 0;
        items.forEach((li) => {
          const id = li.dataset.productId;
          if (id && this.seenOut.has(id)) return;
          if (id) this.seenOut.add(id);
          // 30 Sep 2026: a coral in a timed drop is filed under In Stock (see fetchComing).
          if (li.dataset.coming === '1') return;
          frag.appendChild(document.importNode(this.deferCardImages(li), true));
          added += 1;
        });
        if (added) {
          this.outGrid.appendChild(frag);
          this.stripQuickAdds(this.outGrid);
          this.refreshGrid(this.outGrid);
          this.revealOutPanel();
        }
        // A SHORT page means we've reached the end — there's no reliable page count for a
        // filtered set, so the pages themselves tell us when to stop.
        //
        // 2026-08-23: deliberately no longer stopping on "added === 0" as well. A full page of
        // duplicates does not mean the end of the catalogue, it means this page had already been
        // fetched — exactly what happens if the search box starts loadAllOutOfStock() while a
        // lazy page is in flight. Treating it as the end latched outDone in the middle of a big
        // collection and silently dropped every remaining out-of-stock product from the page,
        // and from the search the shopper had just started.
        if (items.length < 48) this.outDone = true;
        this.nextPage += 1;
      } catch (e) {
        console.warn('[fish-panels] out-of-stock page load failed', e);
        this.outDone = true;
      } finally {
        this.loading = false;
        if (this.outStatus) this.outStatus.textContent = '';
        if (this.outDone) {
          if (this.observer) this.observer.disconnect();
        } else {
          if (!this.observer) this.startOutObserver();
          // Keep filling until the panel actually overflows and is scrollable.
          window.requestAnimationFrame(() => {
            if (this.sentinelWithinReach()) this.loadNextOutPage();
          });
        }
      }
    }

    // Show the Out of Stock heading + intro only once there is something to show.
    revealOutPanel() {
      if (this.outSection && this.outSection.hasAttribute('hidden')) {
        this.outSection.removeAttribute('hidden');
      }
    }

    // Pull in every remaining out-of-stock page in one go (used when a search starts, so
    // search covers the full catalogue rather than only the lazy-loaded cards). Runs once.
    // Wait for any in-flight lazy page to settle. Bounded, so a hung request cannot block the
    // search path indefinitely.
    async waitForOutIdle() {
      for (let i = 0; i < 100 && this.loading; i += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 50));
      }
    }

    async loadAllOutOfStock() {
      if (this._outAllLoaded || this._loadingAllOut) return;
      this._loadingAllOut = true;
      // Take over from the lazy scroll loader so they don't race on the same pages.
      if (this.observer) this.observer.disconnect();
      // Disconnecting the observer does not cancel a request already on its way, and the lazy
      // loader re-arms itself through requestAnimationFrame. Wait for it, then hold the same
      // mutex for the duration so the two can never ask for the same page at once.
      await this.waitForOutIdle();
      this.loading = true;
      if (this.outStatus) this.outStatus.textContent = 'Searching everything…';
      try {
        const MAX_PAGES = 30;
        while (!this.outDone && this.nextPage <= MAX_PAGES) {
          let doc;
          try {
            doc = await this.fetchPage(this.nextPage, 'filter.v.availability=0');
          } catch (e) {
            break;
          }
          const items = doc.querySelectorAll('[data-fish-grid="out-of-stock"] > li.product-grid__item');
          const frag = document.createDocumentFragment();
          let added = 0;
          items.forEach((li) => {
            const id = li.dataset.productId;
            if (id && this.seenOut.has(id)) return;
            if (id) this.seenOut.add(id);
            if (li.dataset.coming === '1') return;
            frag.appendChild(document.importNode(this.deferCardImages(li), true));
            added += 1;
          });
          if (added) {
            this.outGrid.appendChild(frag);
            this.stripQuickAdds(this.outGrid);
            this.refreshGrid(this.outGrid);
            this.revealOutPanel();
            this.updateSearchState();
          }
          if (items.length < 48) this.outDone = true;
          this.nextPage += 1;
        }
        this._outAllLoaded = true;
      } finally {
        this.loading = false;
        this._loadingAllOut = false;
        if (this.outStatus) this.outStatus.textContent = '';
      }
    }
  }

  window.customElements.define('fish-collection-panels', FishCollectionPanels);
})();
